import InvoiceAutomationOutOfScopeTypeEnum from '../Enums/InvoiceAutomationOutOfScopeTypeEnum';
import InvoiceAutomationRunChangeTypeEnum from '../Enums/InvoiceAutomationRunChangeTypeEnum';
import InvoiceAutomationRunOutcomeEnum from '../Enums/InvoiceAutomationRunOutcomeEnum';
import MetricTileToneEnum from '../Enums/MetricTileToneEnum';
import { formatMonthLabel } from './invoiceAutomationMonthHelper';
import moneyHelper from './moneyHelper';

// The amounts of an added job's line, as the run records them, in the order the invoice lists them. The fieldwork fee
// is in the job's pricing currency, the others in the line's own.
const JobAddedAmounts = [
  { field: 'ClientFee', label: 'Client fee', isInPricingCurrency: false },
  { field: 'AdditionalClientFee', label: 'Additional client fee', isInPricingCurrency: false },
  { field: 'FieldworkFee', label: 'Fieldwork fee', isInPricingCurrency: true },
  { field: 'Reimbursement', label: 'Reimbursement', isInPricingCurrency: false },
  { field: 'Travel', label: 'Travel', isInPricingCurrency: false },
  { field: 'Gaming', label: 'Gaming', isInPricingCurrency: false },
  { field: 'Misc', label: 'Misc', isInPricingCurrency: false },
];

// "1 job", "42 jobs".
export function formatCount(count: number, singular: string, plural: string) {
  return `${count} ${count === 1 ? singular : plural}`;
}

// The line over Run Now's preview: while it is worked out again, when it could not be worked out for what is ticked
// now (the previous answer stays under it, and Run waits), or what the run would do.
export function getRunPreviewLead(isUpdating: boolean, isOutdated: boolean) {
  if (isUpdating) {
    return 'Working out what the run would do with what you ticked…';
  }
  if (isOutdated) {
    return (
      'The run could not be worked out for what is ticked now, so below is the previous answer and Run waits. ' +
      'Change what is ticked to try again.'
    );
  }
  return 'What the run would do now. Nothing is created or changed until you click Run.';
}

// The tiles of what Run Now is about to do, from the run preview of what is ticked. Jobs that cannot be added show
// amber.
export function getRunPreviewTiles(preview) {
  return [
    { label: 'Drafts to create', value: preview.draftsToCreate },
    { label: 'Jobs to add', value: preview.jobsToAdd },
    ...getRemovedJobTiles('Jobs to remove', preview.jobsToRemove),
    { label: 'Out of scope', value: preview.jobsOutOfScope },
    ...getWaitingJobTiles(preview.jobsWaiting),
    { label: 'Cannot be added', value: preview.jobsToSkip, tone: getCountTone(preview.jobsToSkip) },
  ];
}

// The same tiles for what a finished run did. A run read back from the History has no count of the jobs that were
// out of scope, as runs do not store it, so that tile is left out for it.
export function getRunResultTiles(result) {
  const hasOutOfScopeCount = result.jobsOutOfScope !== undefined && result.jobsOutOfScope !== null;
  const outOfScopeTiles = hasOutOfScopeCount ? [{ label: 'Out of scope', value: result.jobsOutOfScope }] : [];
  return [
    { label: 'Drafts created', value: result.draftsCreated },
    { label: 'Jobs added', value: result.jobsAdded },
    ...getRemovedJobTiles('Jobs removed', result.jobsRemoved),
    ...outOfScopeTiles,
    ...getWaitingJobTiles(result.jobsWaiting),
    { label: 'Could not be added', value: result.jobsSkipped, tone: getCountTone(result.jobsSkipped) },
  ];
}

// A tile for the late jobs with no open draft to go on yet, as the current month's draft is already finalized, only
// when there are any. Plain, not amber: they go on next month's draft by themselves. A run read back from the History
// has no such count, as runs do not store it.
function getWaitingJobTiles(waitingJobCount?: number | null) {
  if (!waitingJobCount) {
    return [];
  }
  return [{ label: 'Waiting for next month', value: waitingJobCount }];
}

// A tile for the jobs a run takes off its drafts because they no longer pass the Ready for Invoicing rules, only when
// there are any: most runs remove none.
function getRemovedJobTiles(label: string, removedJobCount?: number | null) {
  if (!removedJobCount) {
    return [];
  }
  return [{ label, value: removedJobCount, tone: getCountTone(removedJobCount) }];
}

// The line under a draft Run Now would make or add to: a new draft, or the open draft the jobs go on, the jobs among
// them of an earlier month whose draft was finalized, and the out-of-scope jobs ticked to come in.
export function getPreviewDraftNote(draft) {
  const monthLabel = formatMonthLabel(draft.jobMonth);
  const parts = [
    draft.tpClientInvoiceId ? `Draft ${draft.tpClientInvoiceId} of ${monthLabel}` : `A new draft for ${monthLabel}`,
  ];
  if (draft.lateJobs > 0) {
    parts.push(`${formatCount(draft.lateJobs, 'job comes', 'jobs come')} late, from a month whose draft was finalized`);
  }
  if (draft.broughtInJobs > 0) {
    parts.push(`${formatCount(draft.broughtInJobs, 'job', 'jobs')} brought in`);
  }
  return parts.join(' · ');
}

// The value of a line's box in Run Now's out-of-scope list: its kind, and for a cancelled month the month too.
export function getOutOfScopeGroupKey(group) {
  if (group.outOfScopeType === InvoiceAutomationOutOfScopeTypeEnum.MonthCancelled) {
    return `${group.outOfScopeType}:${group.cancelledMonth}`;
  }
  return group.outOfScopeType;
}

// What ticking a line of Run Now's out-of-scope list does.
export function getOutOfScopeGroupLabel(group) {
  if (group.outOfScopeType === InvoiceAutomationOutOfScopeTypeEnum.TakenOff) {
    return 'Bring in the jobs a person took off a draft';
  }
  if (group.outOfScopeType === InvoiceAutomationOutOfScopeTypeEnum.BeforeFirstMonth) {
    return 'Bring in the jobs of months before the first month';
  }
  return `Make the ${formatMonthLabel(group.cancelledMonth)} draft again`;
}

// What the ticked lines of Run Now's out-of-scope list ask of the run: the cancelled months to make again and the
// kinds of out-of-scope jobs to bring in.
export function getRunChoices(tickedGroupKeys: string[]) {
  const monthKeyStart = `${InvoiceAutomationOutOfScopeTypeEnum.MonthCancelled}:`;
  const monthKeys = tickedGroupKeys.filter((key) => key.startsWith(monthKeyStart));
  return {
    monthsToRemake: monthKeys.map((key) => key.slice(monthKeyStart.length)),
    outOfScopeTypesToBringIn: tickedGroupKeys.filter((key) => !key.startsWith(monthKeyStart)),
  };
}

// The badge of a run's outcome: green Succeeded, red Failed, blue Running.
export function getRunOutcomeBadge(outcome?: string | null) {
  if (outcome === InvoiceAutomationRunOutcomeEnum.Succeeded) {
    return { variant: 'completed', label: 'Succeeded' };
  }
  if (outcome === InvoiceAutomationRunOutcomeEnum.Failed) {
    return { variant: 'failed', label: 'Failed' };
  }
  if (outcome === InvoiceAutomationRunOutcomeEnum.Running) {
    return { variant: 'progress', label: 'Running' };
  }
  return { variant: 'todo', label: outcome ?? '' };
}

// The headline of a run's result. A run that did not start at all has no run id: nothing was recorded. A run still
// marked Running in the History either is running or was cut off before it could record its outcome.
export function getRunResultHeadline(result) {
  if (result.outcome === InvoiceAutomationRunOutcomeEnum.Succeeded) {
    return 'The run is done.';
  }
  if (!result.invoiceAutomationRunId) {
    return 'The run did not start.';
  }
  if (result.outcome === InvoiceAutomationRunOutcomeEnum.Running) {
    return 'The run has not finished.';
  }
  return 'The run stopped on an error.';
}

// What a change of a run says beyond its type: for an added job, the fee and expenses its new line got, from the
// JSON the run recorded, each with its currency code — the line's (currencyCode) for the fees and expenses, the job's
// pricing currency (pricingCurrencyCode) for the fieldwork fee, and none when a code is missing; for any other change,
// the reason or the old and new value the run recorded.
export function getRunChangeDetail(
  changeType?: string | null,
  detail?: string | null,
  currencyCode?: string | null,
  pricingCurrencyCode?: string | null
) {
  if (changeType !== InvoiceAutomationRunChangeTypeEnum.JobAdded) {
    return detail ?? '';
  }

  let lineValues;
  try {
    lineValues = JSON.parse(detail ?? '');
  } catch (e) {
    return detail ?? '';
  }
  const amounts = JobAddedAmounts.filter((amount) => {
    const value = Number(lineValues?.[amount.field]);
    return !Number.isNaN(value) && value !== 0;
  }).map((amount) => {
    const amountCurrencyCode = amount.isInPricingCurrency ? pricingCurrencyCode : currencyCode;
    return `${amount.label} ${moneyHelper.toMoneyFormat(lineValues[amount.field], amountCurrencyCode)}`;
  });
  if (amounts.length === 0) {
    return 'No fee or expenses.';
  }
  return amounts.join(', ');
}

// The jobs a run could not add, each with its id on its platform and the reason the run recorded.
export function getSkippedJobs(runDetail) {
  const changes = runDetail?.changes ?? [];
  return changes
    .filter((change) => change.changeType === InvoiceAutomationRunChangeTypeEnum.JobSkipped)
    .map((change) => ({ tpJobId: change.tpJobId, thirdPartyJobId: change.thirdPartyJobId, reason: change.detail }));
}

// The jobs a run took off its drafts because they no longer passed the Ready for Invoicing rules, each with its id on
// its platform, the draft and the reason the run recorded.
export function getRemovedJobs(runDetail) {
  const changes = runDetail?.changes ?? [];
  return changes
    .filter((change) => change.changeType === InvoiceAutomationRunChangeTypeEnum.JobRemoved)
    .map((change) => ({
      tpJobId: change.tpJobId,
      thirdPartyJobId: change.thirdPartyJobId,
      tpClientInvoiceId: change.tpClientInvoiceId,
      reason: change.detail,
    }));
}

// Amber for jobs that could not be added, when there are any.
function getCountTone(problemCount: number) {
  return problemCount > 0 ? MetricTileToneEnum.Warn : MetricTileToneEnum.Plain;
}
